import { Box, Stack } from '@mui/material';
import { Route, Switch } from 'react-router-dom';

import { EntityType } from './types';
import { CONTENT, getEntityType } from './utils';
import { withCreated } from './invoicing/format';
import InvoiceDetail from './invoicing/InvoiceDetail';
import InvoiceListCard from './invoicing/InvoiceListCard';
import InvoiceReview from './invoicing/InvoiceReview';
import { useInvoicingSession } from './invoicing/invoicingStore';
import NewInvoiceForm from './invoicing/NewInvoiceForm';
import SummaryCard from './invoicing/SummaryCard';
import { InvoicingBlob, Sender } from './invoicing/types';

import { useMember } from 'modules/common/queries/member';

/**
 * Demo replacement for /bookkeeping/invoicing. Prod renders the Hurdlr invoicing iframe,
 * which is empty in the demo. This component natively rebuilds the overview list view,
 * the New Invoice form, its review-and-send step, and the view of a single invoice, reading
 * from each profile's `invoicing` JSON block. Invoices created in the session are added on top.
 */

function getInvoicing(entityType: EntityType): InvoicingBlob {
    const content = CONTENT[entityType] as { invoicing?: InvoicingBlob };
    return (
        content.invoicing ?? {
            summary: { total_invoiced: 0, total_outstanding: 0, paid_count: 0, unpaid_count: 0, overdue_count: 0 },
            clients: [],
            invoices: [],
        }
    );
}

export default function DemoInvoicing() {
    const { data: member } = useMember();
    const { created } = useInvoicingSession();

    const data = getInvoicing(getEntityType(member));
    const invoices = [...created, ...data.invoices];
    const summary = withCreated(data.summary, created);
    const sender: Sender = {
        businessName: member?.full_business_name || 'Your Business',
        ownerName: [member?.first_name, member?.last_name].filter(Boolean).join(' '),
    };

    return (
        <Switch>
            <Route exact path="/bookkeeping/invoicing">
                <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
                    <Stack spacing={3}>
                        <SummaryCard summary={summary} />
                        <InvoiceListCard invoices={invoices} />
                    </Stack>
                </Box>
            </Route>
            <Route exact path="/bookkeeping/invoicing/new">
                <NewInvoiceForm clients={data.clients} invoices={invoices} sender={sender} />
            </Route>
            <Route exact path="/bookkeeping/invoicing/new/review">
                <InvoiceReview clients={data.clients} sender={sender} />
            </Route>
            <Route exact path="/bookkeeping/invoicing/:invoiceId">
                <InvoiceDetail invoices={invoices} clients={data.clients} sender={sender} />
            </Route>
        </Switch>
    );
}
